import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { Card, CardHeader, CardBody, Row, Col } from "reactstrap"

import { UserRole, ChallengeType, ChallengeState, IProcess } from "@api/schema"
import ChallengeLogo from "@components/challenge/ChallengeLogo"
import ChallengeStateText from "@components/challenge/ChallengeStateText"
import ActionIcon from "@components/common/ActionIcon"
import AuthElement from "@components/common/AuthElement"
import { challengeTranslation } from "@services/challengeHelper"
import { routeWithParams, Routes } from "@services/routes"

interface IProps {
  challengeType: ChallengeType
  selectedProcess: IProcess
  roles: UserRole[]
}
/**
 * The component gives an overview of challenges of the given process filtered by a given challenge-type.
 * And it shows only challenges that can be selected for new proposals (and paused challenges),
 * challenges in other states are in the collection too but not relevant here.
 */
const ProcessChallengeView = (props: IProps): JSX.Element => {
  const { challengeType, selectedProcess, roles } = props
  const { t } = useTranslation("common")

  const filteredChallengesByTypeByState = selectedProcess.challenges?.filter(f => f["@type"] === challengeType && [ChallengeState.Published, ChallengeState.Open, ChallengeState.Paused].includes(f.state))

  return filteredChallengesByTypeByState?.length > 0 && <Card>
    <CardHeader>
      <h3>{challengeTranslation("process.challenges", challengeType, t)}</h3>
      <AuthElement requiredRole={UserRole.ProcessManager} roles={roles}>
        <div role="actions" className="icon-navigation">
          <ActionIcon
            aria-label={challengeTranslation("goto.management.challengeManagement", challengeType, t)}
            className="actionicon"
            href={routeWithParams(Routes.AdminChallengeOverview, { type: ChallengeType.BasicChallenge })}
            title={challengeTranslation("goto.management.challengeManagement", challengeType, t)}
            icon={ChallengeType.Fund === challengeType
              ? "money-bag"
              : "challenge"}
            size={25}
          />
        </div>
      </AuthElement>
    </CardHeader>
    <CardBody className="process-challenge-list-item">
      <p>{challengeTranslation("page.process.index.challengesHelp", challengeType, t)}</p>
      {filteredChallengesByTypeByState
        .map((challenge) => <Row key={"challenge-" + challenge.id.toString()}>
          <Col className="process-challenge-list-item__logo">
            <ChallengeLogo challenge={challenge} />
          </Col>
          <Col className="process-challenge-list-item__text">
            <h5>
              <Link href={Routes.ChallengePage} as={routeWithParams(Routes.ChallengePage, { slug: challenge.slug || challenge.id })}>
                <a
                  aria-label={challengeTranslation("goto.challenges.challengeDetails", challengeType, t, { challengeName: challenge.name })}
                  title={challengeTranslation("goto.challenges.challengeDetails", challengeType, t, { challengeName: challenge.name })}>
                  {challenge.name}
                </a>
              </Link>
            </h5>
            <p><ChallengeStateText state={challenge.state} /></p>
          </Col>
        </Row>
        )
      }
    </CardBody>
  </Card>
}

export default ProcessChallengeView